import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../contexts/LanguageContext';
import { ArrowLeft, MapPin, Phone, Mail, Clock, Send, CheckCircle, AlertCircle, MessageCircle } from 'lucide-react';
import Navbar from '../Navbar';
import Footer from '../Footer';
import { Alert, AlertDescription } from '../ui/alert';
import { formspreeConfig } from '../../utils/formspreeConfig';

const FORMSPREE_ENDPOINT = formspreeConfig.endpoint;

const ContactPage = () => {
  const { language } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const formDisabled = isSubmitting || !FORMSPREE_ENDPOINT;

  const content = {
    en: {
      title: 'Contact Us',
      subtitle: 'Get in touch with our team for custom labeling and packaging solutions',
      form: {
        name: 'Full Name',
        email: 'Email Address',
        company: 'Company Name',
        phone: 'Phone Number',
        subject: 'Subject',
        message: 'Your Message',
        submit: 'Send Message',
        submitting: 'Sending...',
        success: 'Thank you! Your message has been sent successfully. We will get back to you within 24 hours.',
        errors: {
          email: 'Please enter a valid email address',
          message: 'Please enter your message',
          emailRequired: 'Email is required',
          messageRequired: 'Message is required',
          submit: 'Unable to send your message. Please try again or email info@etiteks.com.',
          timeout: 'We could not confirm your message was sent in time. Please try again or email info@etiteks.com.',
          unavailable: 'The contact form is temporarily unavailable. Please email us at'
        }
      },
      info: {
        title: 'Contact Information',
        address: {
          title: 'Address',
          value: 'Maltepe Mah. Topkapı Maltepe Yolu No: 7/F, 34010 Zeytinburnu/Istanbul, Turkey'
        },
        phone: {
          title: 'Phone',
          value: '+90 212 501 42 57'
        },
        whatsapp: {
          title: 'WhatsApp',
          value: '+90 532 257 45 86'
        },
        email: {
          title: 'Email',
          value: 'info@etiteks.com'
        },
        hours: {
          title: 'Business Hours',
          value: 'Mon - Fri: 09:00 - 18:00'
        }
      },
      cta: {
        title: 'Ready to elevate your brand?',
        description: 'Contact us today and discover how our premium labeling solutions can transform your products.'
      }
    },
    tr: {
      title: 'İletişim',
      subtitle: 'Özel etiketleme ve ambalaj çözümleri için ekibimizle iletişime geçin',
      form: {
        name: 'Ad Soyad',
        email: 'E-posta Adresi',
        company: 'Şirket Adı',
        phone: 'Telefon Numarası',
        subject: 'Konu',
        message: 'Mesajınız',
        submit: 'Mesaj Gönder',
        submitting: 'Gönderiliyor...',
        success: 'Teşekkürler! Mesajınız başarıyla gönderildi. 24 saat içinde size geri dönüş yapacağız.',
        errors: {
          email: 'Lütfen geçerli bir e-posta adresi girin',
          message: 'Lütfen mesajınızı girin',
          emailRequired: 'E-posta zorunludur',
          messageRequired: 'Mesaj zorunludur',
          submit: 'Mesajınız gönderilemedi. Lütfen tekrar deneyin veya info@etiteks.com adresine yazın.',
          timeout: 'Mesajınızın gönderildiği zamanında doğrulanamadı. Lütfen tekrar deneyin veya info@etiteks.com adresine yazın.',
          unavailable: 'İletişim formu şu anda kullanılamıyor. Lütfen e-posta ile iletişime geçin:'
        }
      },
      info: {
        title: 'İletişim Bilgileri',
        address: {
          title: 'Adres',
          value: 'Maltepe Mah. Topkapı Maltepe Yolu No: 7/F, 34010 Zeytinburnu/İstanbul, Türkiye'
        },
        phone: {
          title: 'Telefon',
          value: '+90 212 501 42 57'
        },
        whatsapp: {
          title: 'WhatsApp',
          value: '+90 532 257 45 86'
        },
        email: {
          title: 'E-posta',
          value: 'info@etiteks.com'
        },
        hours: {
          title: 'Çalışma Saatleri',
          value: 'Pazartesi - Cuma: 09:00 - 18:00'
        }
      },
      cta: {
        title: 'Markanızı yükseltmeye hazır mısınız?',
        description: 'Bugün bizimle iletişime geçin ve premium etiketleme çözümlerimizin ürünlerinizi nasıl dönüştürebileceğini keşfedin.'
      }
    }
  };

  const t = content[language];

  // Email validation regex
  const isValidEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.email.trim()) {
      newErrors.email = t.form.errors.emailRequired;
    } else if (!isValidEmail(formData.email)) {
      newErrors.email = t.form.errors.email;
    }
    
    if (!formData.message.trim()) {
      newErrors.message = t.form.errors.messageRequired;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formDisabled) return;
    setSubmitError('');
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    const controller = new AbortController();
    const timeoutId = window.setTimeout(() => controller.abort(), 10000);
    
    try {
      const response = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: formData.company,
          phone: formData.phone,
          subject: formData.subject,
          message: formData.message,
          _replyto: formData.email,
          _subject: `New Contact Form Submission: ${formData.subject || 'No Subject'}`
        })
      });
      
      if (response.ok) {
        setSubmitted(true);
        setFormData({ name: '', email: '', company: '', phone: '', subject: '', message: '' });
        setErrors({});
      } else {
        setSubmitError(t.form.errors.submit);
      }
    } catch (error) {
      setSubmitError(error.name === 'AbortError' ? t.form.errors.timeout : t.form.errors.submit);
    } finally {
      window.clearTimeout(timeoutId);
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    setSubmitError('');
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white" data-testid="contact-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative min-h-[350px] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/j2wu35i4_sadasf.png" alt="Custom woven labels produced at the Etiteks factory in Istanbul" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-black/50"></div>
        </div>
        <div className="max-w-7xl mx-auto px-6 relative z-10 pb-12 pt-32">
          <Link 
            to="/" 
            data-testid="contact-home-link"
            className="inline-flex items-center text-blue-200 hover:text-white transition-colors mb-8 group"
          >
            <ArrowLeft size={20} className="mr-2 group-hover:-translate-x-1 transition-transform" />
            {language === 'tr' ? 'Ana Sayfa' : 'Home'}
          </Link>
          
          <h1 data-testid="contact-page-title" className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
            {t.title}
          </h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-5 gap-16">
            
            {/* Contact Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-3xl shadow-xl p-8 md:p-12">
                {submitted ? (
                  <div className="text-center py-12" data-testid="contact-success" role="status">
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                      <CheckCircle size={40} className="text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 mb-4">
                      {language === 'tr' ? 'Mesaj Gönderildi!' : 'Message Sent!'}
                    </h3>
                    <p className="text-gray-600 mb-8">{t.form.success}</p>
                    <button
                      onClick={() => setSubmitted(false)}
                      data-testid="contact-send-another"
                      type="button"
                      className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors"
                    >
                      {language === 'tr' ? 'Yeni Mesaj Gönder' : 'Send Another Message'}
                    </button>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form" aria-busy={isSubmitting}>
                    {!FORMSPREE_ENDPOINT && (
                      <Alert variant="destructive" data-testid="contact-config-error" role="alert">
                        <AlertCircle size={16} />
                        <AlertDescription className="break-words">
                          {t.form.errors.unavailable}{' '}
                          <a href={`mailto:${t.info.email.value}`} className="underline underline-offset-2" data-testid="contact-config-email">
                            {t.info.email.value}
                          </a>.
                        </AlertDescription>
                      </Alert>
                    )}
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="contact-name" className="block text-sm font-medium text-gray-700 mb-2">
                          {t.form.name}
                        </label>
                        <input
                          type="text"
                          name="name"
                          id="contact-name"
                          data-testid="contact-name-input"
                          disabled={formDisabled}
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-email" className="block text-sm font-medium text-gray-700 mb-2">
                          {t.form.email} <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="email"
                          name="email"
                          id="contact-email"
                          data-testid="contact-email-input"
                          disabled={formDisabled}
                          aria-invalid={Boolean(errors.email)}
                          aria-describedby={errors.email ? 'contact-email-error' : undefined}
                          value={formData.email}
                          onChange={handleChange}
                          className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                            errors.email ? 'border-red-500 bg-red-50' : 'border-gray-300'
                          }`}
                        />
                        {errors.email && (
                          <div id="contact-email-error" data-testid="contact-email-error" role="alert" className="flex items-center mt-2 text-red-600 text-sm">
                            <AlertCircle size={16} className="mr-1" />
                            {errors.email}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="contact-company" className="block text-sm font-medium text-gray-700 mb-2">
                          {t.form.company}
                        </label>
                        <input
                          type="text"
                          name="company"
                          id="contact-company"
                          data-testid="contact-company-input"
                          disabled={formDisabled}
                          value={formData.company}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        />
                      </div>
                      <div>
                        <label htmlFor="contact-phone" className="block text-sm font-medium text-gray-700 mb-2">
                          {t.form.phone}
                        </label>
                        <input
                          type="tel"
                          name="phone"
                          id="contact-phone"
                          data-testid="contact-phone-input"
                          disabled={formDisabled}
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                        />
                      </div>
                    </div>
                    
                    <div>
                      <label htmlFor="contact-subject" className="block text-sm font-medium text-gray-700 mb-2">
                        {t.form.subject}
                      </label>
                      <input
                        type="text"
                        name="subject"
                        id="contact-subject"
                        data-testid="contact-subject-input"
                        disabled={formDisabled}
                        value={formData.subject}
                        onChange={handleChange}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="contact-message" className="block text-sm font-medium text-gray-700 mb-2">
                        {t.form.message} <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        name="message"
                        id="contact-message"
                        data-testid="contact-message-input"
                        disabled={formDisabled}
                        aria-invalid={Boolean(errors.message)}
                        aria-describedby={errors.message ? 'contact-message-error' : undefined}
                        value={formData.message}
                        onChange={handleChange}
                        rows={6}
                        className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all resize-none ${
                          errors.message ? 'border-red-500 bg-red-50' : 'border-gray-300'
                        }`}
                      />
                      {errors.message && (
                        <div id="contact-message-error" data-testid="contact-message-error" role="alert" className="flex items-center mt-2 text-red-600 text-sm">
                          <AlertCircle size={16} className="mr-1" />
                          {errors.message}
                        </div>
                      )}
                    </div>
                    
                    {submitError && (
                      <Alert variant="destructive" data-testid="contact-submit-error" role="alert" aria-live="assertive">
                        <AlertCircle size={16} />
                        <AlertDescription className="break-words">{submitError}</AlertDescription>
                      </Alert>
                    )}

                    <button
                      type="submit"
                      data-testid="contact-submit-button"
                      disabled={formDisabled}
                      className={`w-full py-4 font-semibold rounded-xl flex items-center justify-center gap-2 shadow-lg transition-all ${
                        formDisabled
                          ? 'bg-gray-400 cursor-not-allowed' 
                          : 'bg-blue-600 hover:bg-blue-700 hover:shadow-xl text-white'
                      }`}
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          {t.form.submitting}
                        </>
                      ) : (
                        <>
                          <Send size={20} />
                          {t.form.submit}
                        </>
                      )}
                    </button>
                  </form>
                )}
              </div>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{t.info.title}</h2>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="text-blue-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{t.info.address.title}</h3>
                      <p className="text-gray-600">{t.info.address.value}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="text-blue-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{t.info.phone.title}</h3>
                      <p className="text-gray-600">{t.info.phone.value}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="text-blue-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{t.info.email.title}</h3>
                      <p className="text-gray-600">{t.info.email.value}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="text-blue-600" size={24} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{t.info.hours.title}</h3>
                      <p className="text-gray-600">{t.info.hours.value}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* CTA Card */}
              <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-3xl p-8 text-white">
                <h3 className="text-xl font-bold mb-3">{t.cta.title}</h3>
                <p className="text-blue-100">{t.cta.description}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="py-16 bg-gray-50" data-testid="contact-map">
        <div className="max-w-5xl mx-auto px-6">
          {/* Title with lines */}
          <div className="flex items-center justify-center mb-12">
            <div className="flex-1 border-t border-gray-300"></div>
            <h2 className="px-6 text-lg md:text-xl font-semibold text-gray-700 italic whitespace-nowrap" style={{ fontFamily: 'Georgia, serif' }}>
              {language === 'tr' ? 'Güvenilir Üretim Ortağı' : 'Trusted Manufacturing Partner'}
            </h2>
            <div className="flex-1 border-t border-gray-300"></div>
          </div>

          {/* Map Container */}
          <div className="relative bg-gray-100 rounded-2xl overflow-hidden shadow-md mx-auto max-w-3xl">
            {/* Open in Google Maps Button */}
            <a
              href="https://www.google.com/maps/place/ET%C4%B0TEKS+DOKUMA+ET%C4%B0KET+MATBAACILIK+SANAY%C4%B0+ve+T%C4%B0CARET+LTD.+%C5%9ET%C4%B0./@41.0288706,28.9133757,17z/data=!3m1!4b1!4m6!3m5!1s0x14caba5d6f1c4d65:0x389cde1accfb1fe1!8m2!3d41.0288706!4d28.9159506!16s%2Fg%2F11g022t6f2?entry=ttu&g_ep=EgoyMDI2MDMyOS4wIKXMDSoASAFQAw%3D%3D"
              target="_blank"
              rel="noopener noreferrer"
              className="absolute top-4 right-4 z-10 inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2.5 rounded-lg shadow-lg transition-colors"
              data-testid="open-google-maps-btn"
            >
              <MapPin size={16} />
              Open in Google Maps
            </a>

            {/* Map Image */}
            <img 
              src="https://customer-assets.emergentagent.com/job_63ec9bab-e8ef-40cf-ae2e-d7c59bba5a35/artifacts/udzie1zn_sadadsadz.png" 
              alt="Etiteks Location - Istanbul, Turkey" 
              className="w-full h-auto object-cover"
            />
          </div>

          {/* Location Text */}
          <div className="text-center mt-10">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2" style={{ fontFamily: 'Georgia, serif' }}>
              Istanbul, Turkey
            </h3>
            <p className="text-gray-500 italic text-base">
              {language === 'tr' ? 'Avrupa\'ya yakın. AB genelinde hızlı teslimat.' : 'Close to Europe. Fast delivery across EU.'}
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
